import { motion } from "framer-motion";
import type { HTMLMotionProps } from "framer-motion";
import { Link } from "react-router-dom";
import { forwardRef, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon";
import { hoverNudge } from "@/motion/variants";

type Variant = "primary" | "ghost" | "dark" | "light" | "outline-light" | "glass";
type Size = "sm" | "md" | "lg";

const VARIANT_CLASS: Record<Variant, string> = {
  primary: "btn--primary",
  ghost: "btn--ghost",
  dark: "btn--dark",
  light: "btn--light",
  "outline-light": "btn--outline-light",
  glass: "btn--glass",
};

const SIZE_CLASS: Record<Size, string> = { sm: "btn--sm", md: "", lg: "btn--lg" };

interface CommonProps {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  loading?: boolean;
  /** Trailing glyph — the arrow nudges on hover via CSS. */
  icon?: IconName;
  leadingIcon?: IconName;
  children?: ReactNode;
  className?: string;
}

function classes({ variant = "primary", size = "md", block, className }: CommonProps): string {
  return [
    "btn",
    VARIANT_CLASS[variant],
    SIZE_CLASS[size],
    block ? "btn--block" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

/* ------------------------------------------------------------------ button */
type ButtonProps = CommonProps &
  Omit<HTMLMotionProps<"button">, "children" | "className" | "ref">;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, loading, icon, leadingIcon, children, className, disabled, ...rest },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      type="button"
      className={`${classes({ variant, size, block, className })}${loading ? " is-loading" : ""}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...(disabled || loading ? {} : hoverNudge)}
      {...rest}
    >
      {leadingIcon ? <Icon name={leadingIcon} /> : null}
      {children}
      {icon ? <Icon name={icon} /> : null}
    </motion.button>
  );
});

/* -------------------------------------------------------------- link button */
interface ButtonLinkProps extends CommonProps {
  to: string;
  /** Renders an <a> instead of a router <Link> — for tel:, mailto:, #hash. */
  external?: boolean;
  onClick?: () => void;
  "aria-label"?: string;
}

export function ButtonLink({
  to,
  external,
  variant,
  size,
  block,
  icon,
  leadingIcon,
  children,
  className,
  ...rest
}: ButtonLinkProps) {
  const content = (
    <>
      {leadingIcon ? <Icon name={leadingIcon} /> : null}
      {children}
      {icon ? <Icon name={icon} /> : null}
    </>
  );

  const cls = classes({ variant, size, block, className });

  if (external) {
    return (
      <motion.a href={to} className={cls} {...hoverNudge} {...rest}>
        {content}
      </motion.a>
    );
  }

  return (
    <motion.div style={{ display: block ? "block" : "inline-flex" }} {...hoverNudge}>
      <Link to={to} className={cls} {...rest}>
        {content}
      </Link>
    </motion.div>
  );
}

/* --------------------------------------------------------------- icon button */
interface IconButtonProps extends Omit<HTMLMotionProps<"button">, "children"> {
  name: IconName;
  label: string;
  solid?: boolean;
  className?: string;
}

export function IconButton({ name, label, solid, className, ...rest }: IconButtonProps) {
  return (
    <motion.button
      type="button"
      className={`icon-btn${solid ? " icon-btn--solid" : ""}${className ? ` ${className}` : ""}`}
      aria-label={label}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.94 }}
      transition={{ duration: 0.18 }}
      {...rest}
    >
      <Icon name={name} />
    </motion.button>
  );
}

/* ------------------------------------------------------------- arrow link */
export function LinkArrow({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={to} className={`link-arrow${className ? ` ${className}` : ""}`}>
      {children}
      <Icon name="arrow-right" />
    </Link>
  );
}
